const PAGE_SIZE = 100;

const MAX_ROWS = 5000;

const MAX_BYTES = 8 * 1024 * 1024;

const SCROLL_MARGIN = 8;

const BUTTON_SPINNER = '<svg class="animate-slowspin" width="14" height="14" viewBox="0 0 24 24" fill="none" '
    + 'stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">'
    + '<path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>'
    + '</svg>';

const VALIDATE_CHUNK_SIZE = 500;

const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 200;

function detectDelimiter(text) {
    const headerLine = text.split(/\r?\n/, 1)[0] ?? '';

    let commas = 0;
    let semicolons = 0;
    let inQuote = false;

    for (let i = 0; i < headerLine.length; i++) {
        const ch = headerLine[i];

        if (ch === '"') { inQuote = !inQuote; continue; }
        if (inQuote) continue;
        if (ch === ',') commas++;
        else if (ch === ';') semicolons++;
    }

    return semicolons > commas ? ';' : ',';
}

const isBlankRow = (row) => row.every(value => value === '');

function parseCsv(text) {
    const delimiter = detectDelimiter(text);
    const rows = [];
    let row = [];
    let cur = '';
    let inQuote = false;
    let sawAnyChar = false;

    const endField = () => { row.push(cur.trim()); cur = ''; };
    const endRow = () => {
        endField();
        rows.push(row);
        row = [];
    };

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        sawAnyChar = true;

        if (inQuote) {
            if (ch === '"' && text[i + 1] === '"') { cur += '"'; i++; }
            else if (ch === '"') inQuote = false;
            else cur += ch;
            continue;
        }

        if (ch === '"') { inQuote = true; }
        else if (ch === delimiter) { endField(); }
        else if (ch === '\r') { }
        else if (ch === '\n') { endRow(); }
        else cur += ch;
    }

    if (cur !== '' || row.length || (sawAnyChar && !rows.length)) endRow();

    while (rows.length && isBlankRow(rows[rows.length - 1])) rows.pop();

    if (!rows.length) return { headers: [], rows: [] };

    const headers = rows[0];

    return { headers, rows: rows.slice(1).filter(dataRow => !isBlankRow(dataRow)) };
}

function canonicalHeader(header) {
    return String(header ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

const NO_EXTENSION_ATTRS = [
    ['data-gramm', 'false'],
    ['data-gramm_editor', 'false'],
    ['data-enable-grammarly', 'false'],
    ['data-lpignore', 'true'],
    ['data-1p-ignore', ''],
    ['data-bwignore', ''],
    ['data-np-ignore', ''],
    ['data-dashlane-ignore', 'true'],
    ['data-form-type', 'other'],
    ['spellcheck', 'false'],
    ['autocorrect', 'off'],
    ['autocapitalize', 'off'],
];

function placeCaretAtEnd(element) {
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
}

const EMPTY_ROW_SET = new Set();

function normalizePhoneValue(value) {
    const digits = String(value ?? '').replace(/[^\d+]/g, '');

    if (digits === '') return '';

    return digits.startsWith('+') ? digits : `+${digits}`;
}

const CROSS_ROW_NORMALIZERS = {
    phone: normalizePhoneValue,
    lower: (value) => String(value ?? '').trim().toLowerCase(),
    plain: (value) => String(value ?? '').trim(),
};

function crossRowNormalizer(name) {
    return CROSS_ROW_NORMALIZERS[name ?? 'plain'] ?? CROSS_ROW_NORMALIZERS.plain;
}

function canonicalNames(names) {
    return names.filter(Boolean).map(canonicalHeader);
}

function compileCrossRowRule(rule) {
    const keyParts = rule.keyColumns ?? [{ column: rule.column, normalize: rule.normalize }];

    return {
        keyColumns: keyParts.map(part => ({
            names:     canonicalNames([part.column, ...(part.columnAliases ?? [])]),
            normalize: crossRowNormalizer(part.normalize),
        })),
        errorColumns: canonicalNames([
            rule.errorColumn ?? keyParts[0].column,
            ...(rule.errorColumnAliases ?? []),
        ]),
        scopeColumns:   canonicalNames([rule.scopeColumn, ...(rule.scopeColumnAliases ?? [])]),
        normalizeScope: crossRowNormalizer(rule.scopeNormalize ?? rule.normalize),
        message:        rule.message,
        preferServerError: rule.preferServerError === true,
    };
}

function buildErrorIndex(errors) {
    const rowsByColumn = new Map();
    const errorRows    = new Set();

    for (const error of errors) {
        if (error.kind === 'sheet') continue;

        errorRows.add(error.r);

        let columnRows = rowsByColumn.get(error.c);

        if (!columnRows) {
            columnRows = new Set();
            rowsByColumn.set(error.c, columnRows);
        }

        columnRows.add(error.r);
    }

    return { rowsByColumn, errorRows };
}

function scopeRowSet(ctx) {
    const errorIndex = ctx.errorIndex();

    return ctx.filterCol !== null
        ? (errorIndex.rowsByColumn.get(ctx.filterCol) ?? EMPTY_ROW_SET)
        : errorIndex.errorRows;
}

function rowMatchesTerm(row, term) {
    if (!row) return false;

    for (const value of row) {
        if (String(value ?? '').toLowerCase().includes(term)) return true;
    }

    return false;
}

function searchRowSet(ctx) {
    const term = String(ctx.search ?? '').trim().toLowerCase();

    if (term === '') return null;

    const cache = ctx._searchCache;

    if (cache && cache.term === term && cache.version === ctx._rowsVersion) {
        return cache.matches;
    }

    const matches = new Set();

    ctx.rows.forEach((row, rowIndex) => {
        if (rowMatchesTerm(row, term)) matches.add(rowIndex);
    });

    ctx._searchCache = { term, version: ctx._rowsVersion, matches };

    return matches;
}

function buildFilteredRowIndexes(ctx) {
    const matches        = searchRowSet(ctx);
    const errorFilterOff = !ctx.filterErrors && ctx.filterCol === null;

    if (errorFilterOff && matches === null) {
        return ctx.rows.map((_, r) => r);
    }

    const kept = [];

    if (errorFilterOff) {
        for (let rowIndex = 0; rowIndex < ctx.rows.length; rowIndex++) {
            if (matches.has(rowIndex)) kept.push(rowIndex);
        }

        return kept;
    }

    const live = scopeRowSet(ctx);
    const keep = ctx.filterKeep ?? live;

    for (let rowIndex = 0; rowIndex < ctx.rows.length; rowIndex++) {
        if (matches !== null && !matches.has(rowIndex)) continue;

        if (keep.has(rowIndex) || live.has(rowIndex)) kept.push(rowIndex);
    }

    return kept;
}

function filteredRowIndexes(ctx) {
    const term  = String(ctx.search ?? '').trim().toLowerCase();
    const cache = ctx._filteredCache;

    if (cache
        && cache.version      === ctx._rowsVersion
        && cache.term         === term
        && cache.filterErrors === ctx.filterErrors
        && cache.filterCol    === ctx.filterCol
        && cache.filterKeep   === ctx.filterKeep
        && cache.errors       === ctx.errors
        && cache.rowCount     === ctx.rows.length) {
        return cache.list;
    }

    const list = buildFilteredRowIndexes(ctx);

    ctx._filteredCache = {
        version:      ctx._rowsVersion,
        term,
        filterErrors: ctx.filterErrors,
        filterCol:    ctx.filterCol,
        filterKeep:   ctx.filterKeep,
        errors:       ctx.errors,
        rowCount:     ctx.rows.length,
        list,
        offsets:      null,
    };

    return list;
}

function filteredRowOffset(ctx, rowIndex) {
    const list  = filteredRowIndexes(ctx);
    const cache = ctx._filteredCache;

    if (!cache.offsets) {
        const offsets = new Map();

        for (let offset = 0; offset < list.length; offset++) offsets.set(list[offset], offset);

        cache.offsets = offsets;
    }

    return cache.offsets.get(rowIndex) ?? -1;
}

function visibleRowIndexes(ctx) {
    const filtered = filteredRowIndexes(ctx);
    if (filtered.length <= ctx.pageSize) return filtered;
    const start = ctx.page * ctx.pageSize;
    return filtered.slice(start, start + ctx.pageSize);
}

export default function csvReviewGrid(options = {}) {
    const entityLabel       = options.entityLabel ?? 'Main Event';
    const entityLabelPlural = options.entityLabelPlural ?? `${entityLabel}s`;
    const entityListLabel   = options.entityListLabel ?? entityLabelPlural.toLowerCase();

    const duplicateColumns  = new Set(
        ['code', ...(options.duplicateColumns ?? [])].map(canonicalHeader),
    );

    const crossRowDuplicates = (options.crossRowDuplicates ?? []).map(compileCrossRowRule);

    const maxRows = options.maxRows ?? MAX_ROWS;

    const esc = (s) =>
        String(s).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[m]);

    return {
        reviewStep:      'none',
        fileReady:       false,
        fileName:        '',
        fileError:       '',
        headers:         [],
        rows:            [],
        errors:          [],
        sheetError:      '',
        importError:     '',
        cursor:          -1,
        sel:             null,
        range:           null,
        rangeBlocks:     [],
        rangeExcluded:   new Set(),
        rangeFocus:      null,
        flagHover:       null,
        search:          '',
        filterErrors:    false,
        filterCol:       null,
        filterKeep:      null,
        importedCount:   0,
        page:            0,
        pageSize:        PAGE_SIZE,
        totalPageCount:  1,
        importBatchId:   null,
        importTotal:     0,
        importDone:      0,
        importChunks:    0,
        busy:            false,
        gridLoading:     false,
        _importPoll:     null,
        _pollAttempts:   0,
        _pollGeneration: 0,
        _serverRowErrors: new Map(),
        _errorIndexCache:  null,
        _errorIndexSource: null,
        _rowsVersion:      0,
        _searchCache:      null,
        _filteredCache:    null,
        _selectionIndexCache: null,
        _validationRun:    0,
        _noColumnFastPath: new Set(),

        init() {
            this._onResize = () => {
                if (this.reviewStep === 'grid') this.renderCellPop();
            };
            window.addEventListener('resize', this._onResize);

            this.$watch('reviewStep', async (val) => {
                if (val === 'grid') {
                    await this.$nextTick();
                    this._bindGridListeners();
                }
            });
        },

        destroy() {
            this._stopPolling();
            this._unbindGridListeners();
            if (this._onResize) window.removeEventListener('resize', this._onResize);
        },

        readFile(input) {
            if (!input.files || !input.files[0]) return;
            const file = input.files[0];
            this.fileReady = false;
            this.fileError = '';

            if (file.size > MAX_BYTES) {
                this.fileError = `This file is ${(file.size / 1048576).toFixed(1)} MB. `
                    + `The review grid holds the whole file in memory, so the limit is ${MAX_BYTES / 1048576} MB.`;
                this._resetFile();
                return;
            }

            const reader = new FileReader();

            reader.onerror = () => {
                this.fileError = 'This file could not be read. Please try again.';
                this._resetFile();
            };

            reader.onload = (e) => {
                let parsed;
                try {
                    parsed = parseCsv(e.target.result);
                } catch {
                    this.fileError = 'This file could not be parsed as CSV.';
                    this._resetFile();
                    return;
                }

                if (!parsed.headers.length) {
                    this.fileError = 'This file is empty.';
                    this._resetFile();
                    return;
                }

                if (parsed.rows.length > maxRows) {
                    this.fileError = `This file has ${parsed.rows.length} rows. `
                        + `Please split it into files of ${maxRows} rows or fewer.`;
                    this._resetFile();
                    return;
                }

                this.fileName  = file.name;
                this.headers   = parsed.headers;
                this.rows      = parsed.rows;
                this.fileReady = true;
                this._noColumnFastPath.clear();
                this._markRowsChanged();
            };

            reader.readAsText(file);
        },

        async openReview() {
            this.sel          = null;
            this.search       = '';
            this._resetSelection();
            this.cursor       = -1;
            this.filterErrors = false;
            this.filterCol    = null;
            this.filterKeep   = null;
            this.errors       = [];
            this.sheetError   = '';
            this.importError  = '';
            this.page         = 0;
            this._serverRowErrors = new Map();
            this.reviewStep   = 'grid';
            this.gridLoading  = true;

            await this.$nextTick();

            this.render();
            this.announce(`Validating ${this.rows.length} rows…`);

            try {
                await this.validateAll();
            } finally {
                this.gridLoading = false;
                this.render();
            }

            this.announce(
                `${this.rows.length} rows loaded, ${this.errors.length} unresolved errors.`
            );
            this.$refs.overlay?.focus();
        },

        cancelReview() {
            this._stopPolling();
            this.reviewStep = 'none';
        },

        closeAll() {
            this._stopPolling();
            this.reviewStep = 'none';
            this._resetFile();
            this.$wire.cancel();
        },

        get importInFlight() {
            return this.importBatchId !== null;
        },

        get importPercent() {
            if (!this.importChunks) return 0;
            return Math.min(100, Math.round((this.importDone / this.importChunks) * 100));
        },

        dismissImporting() {
            this._stopPolling();
            this.importBatchId = null;
            this.importChunks  = 0;
            this.importDone    = 0;
            this.importTotal   = 0;
            this.importError   = '';
            this.announce('The import is still running in the background.');
            this.closeAll();
        },

        onImportComplete(count) {
            this._stopPolling();
            this.importedCount   = count;
            this.importError     = '';
            this.importBatchId   = null;
            this.importChunks    = 0;
            this.importDone      = 0;
            this.reviewStep      = 'done';
            if (this.$refs.resultTitle) {
                this.$refs.resultTitle.textContent =
                    count === 1 ? `1 ${entityLabel} imported successfully`
                                : `${count} ${entityLabelPlural} imported successfully`;
            }
            this._resetFile();
        },

        _resetFile() {
            this.fileReady = false;
            this.fileName  = '';
            this.headers   = [];
            this.rows      = [];
            this.search    = '';
            this._resetSelection();
            this._markRowsChanged();
            const input = (this.$root ?? document).querySelector('#csv-dz');
            if (input) input.value = '';
        },

        async validateAll() {
            let errors;
            try {
                errors = await this.$wire.validateRows(this.headers, this.rows);
            } catch {
                this.importError = 'Validation could not be completed. Please try again.';
                return;
            }

            this._absorbServerErrors(errors, null);
            this._rebuildErrors();

            if (this.cursor >= this.errors.length) {
                this.cursor = this.errors.length - 1;
            }
            this.render();
        },

        async validateEditedRow(rowIndex) {
            let errors;
            try {
                errors = await this.$wire.validateSingleRow(this.headers, this.rows[rowIndex], rowIndex);
            } catch {
                this.importError = 'Validation could not be completed. Please try again.';
                return;
            }

            this._absorbServerErrors(errors, rowIndex);
            this._rebuildErrors();
        },

        _absorbServerErrors(errors, rowIndex) {
            const list  = Array.isArray(errors) ? errors : [];
            const sheet = list.find(e => e.kind === 'sheet');
            const scoped = rowIndex === null
                ? null
                : (Array.isArray(rowIndex) ? rowIndex : [rowIndex]);

            if (scoped === null) {
                this.sheetError = sheet ? sheet.msg : '';
                this._serverRowErrors = new Map();
            } else if (sheet) {
                this.sheetError = sheet.msg;
            }

            if (sheet) return;

            if (scoped !== null) {
                for (const index of scoped) this._serverRowErrors.delete(index);
            }

            const clientTrackedColumns = this._clientTrackedDuplicateColumns();

            for (const error of list) {
                if (error.kind === 'duplicate' && clientTrackedColumns.has(error.c)) continue;

                const bucket = this._serverRowErrors.get(error.r) ?? [];
                bucket.push({ c: error.c, msg: error.msg, kind: error.kind });
                this._serverRowErrors.set(error.r, bucket);
            }
        },

        async _validateColumnFill(columnIndex, rowIndexes) {
            if (this._noColumnFastPath.has(columnIndex)) return false;

            const groupsByValue = new Map();

            for (const rowIndex of rowIndexes) {
                const value = String(this.rows[rowIndex][columnIndex] ?? '');

                let group = groupsByValue.get(value);

                if (!group) {
                    group = { row: this.rows[rowIndex], rows: [] };
                    groupsByValue.set(value, group);
                }

                group.rows.push(rowIndex);
            }

            const run = ++this._validationRun;

            let result;

            try {
                result = await this.$wire.validateColumnFill(this.headers, columnIndex, [...groupsByValue.values()]);
            } catch {
                if (run !== this._validationRun) return true;

                this.importError = 'Validation could not be completed. Please try again.';

                return true;
            }

            if (run !== this._validationRun) return true;

            if (!result || result.ok !== true) {
                this._noColumnFastPath.add(columnIndex);

                return false;
            }

            this._absorbColumnErrors(result.errors, columnIndex, rowIndexes);
            this._rebuildErrors();

            return true;
        },

        _absorbColumnErrors(errors, columnIndex, rowIndexes) {
            const list  = Array.isArray(errors) ? errors : [];
            const sheet = list.find(error => error.kind === 'sheet');

            if (sheet) {
                this.sheetError = sheet.msg;

                return;
            }

            for (const rowIndex of rowIndexes) {
                const bucket = this._serverRowErrors.get(rowIndex);

                if (!bucket) continue;

                const kept = bucket.filter(error => error.c !== columnIndex);

                if (kept.length) this._serverRowErrors.set(rowIndex, kept);
                else this._serverRowErrors.delete(rowIndex);
            }

            const clientTrackedColumns = this._clientTrackedDuplicateColumns();

            for (const error of list) {
                if (error.kind === 'duplicate' && clientTrackedColumns.has(error.c)) continue;

                const bucket = this._serverRowErrors.get(error.r) ?? [];

                bucket.push({ c: error.c, msg: error.msg, kind: error.kind });
                this._serverRowErrors.set(error.r, bucket);
            }
        },

        async validateEditedRows(rowIndexes) {
            const unique = [...new Set(rowIndexes)].sort((a, b) => a - b);

            if (!unique.length) return;

            const run = ++this._validationRun;

            for (let start = 0; start < unique.length; start += VALIDATE_CHUNK_SIZE) {
                const chunk   = unique.slice(start, start + VALIDATE_CHUNK_SIZE);
                const entries = chunk.map(rowIndex => ({ r: rowIndex, row: this.rows[rowIndex] }));

                let errors;

                try {
                    errors = await this.$wire.validateRowsSubset(this.headers, entries);
                } catch {
                    if (run !== this._validationRun) return;

                    this.importError = 'Validation could not be completed. Please try again.';

                    return;
                }

                if (run !== this._validationRun) return;

                this._absorbServerErrors(errors, chunk);
            }

            this._rebuildErrors();
        },

        _duplicateColumn() {
            return this.headers.findIndex(header => duplicateColumns.has(canonicalHeader(header)));
        },

        _columnIndexIn(names) {
            if (names.length === 0) return -1;

            return this.headers.findIndex(header => names.includes(canonicalHeader(header)));
        },

        _clientTrackedDuplicateColumns() {
            const tracked = new Set();
            const codeCol = this._duplicateColumn();

            if (codeCol !== -1) tracked.add(codeCol);

            for (const rule of crossRowDuplicates) {
                const column = this._columnIndexIn(rule.errorColumns);

                if (column !== -1) tracked.add(column);
            }

            return tracked;
        },

        _crossRowDuplicateErrors() {
            if (crossRowDuplicates.length === 0) return [];

            const out = [];

            for (const rule of crossRowDuplicates) {
                const keyColumns = rule.keyColumns.map(part => this._columnIndexIn(part.names));

                if (keyColumns.includes(-1)) continue;

                const errorColumn = this._columnIndexIn(rule.errorColumns);

                if (errorColumn === -1) continue;

                const scopeColumn = this._columnIndexIn(rule.scopeColumns);
                const scopeByKey  = new Map();

                this.rows.forEach((row, r) => {
                    const parts = [];

                    for (let part = 0; part < keyColumns.length; part++) {
                        const value = rule.keyColumns[part].normalize(row[keyColumns[part]]);

                        if (value === '') return;

                        parts.push(value);
                    }

                    const key   = parts.join('|');
                    const scope = scopeColumn === -1 ? '' : rule.normalizeScope(row[scopeColumn]);

                    if (scopeColumn !== -1 && scope !== '' && key === scope) return;

                    if (!scopeByKey.has(key)) {
                        scopeByKey.set(key, scope);

                        return;
                    }

                    if (scopeColumn === -1 || scopeByKey.get(key) !== scope) {
                        out.push({
                            r,
                            c:                 errorColumn,
                            msg:               rule.message,
                            kind:              'duplicate',
                            preferServerError: rule.preferServerError,
                        });
                    }
                });
            }

            return out;
        },

        _duplicateErrors() {
            const codeCol = this._duplicateColumn();
            if (codeCol === -1) return [];

            const counts = new Map();
            this.rows.forEach(row => {
                const value = String(row[codeCol] ?? '').trim();
                if (value !== '') counts.set(value, (counts.get(value) ?? 0) + 1);
            });

            const out = [];
            this.rows.forEach((row, r) => {
                const value = String(row[codeCol] ?? '').trim();
                if (value !== '' && counts.get(value) > 1) {
                    out.push({ r, c: codeCol, msg: `The ${String(this.headers[codeCol] ?? 'code').toLowerCase().replace(/_/g, ' ')} has already been taken.`, kind: 'duplicate' });
                }
            });
            return out;
        },

        _rebuildErrors() {
            if (this.sheetError) {
                this.errors = [];
                return;
            }

            const map = new Map();

            for (const error of this._duplicateErrors()) {
                map.set(`${error.r}:${error.c}`, error);
            }

            const crossRowErrors = this._crossRowDuplicateErrors();

            for (const error of crossRowErrors) {
                if (error.preferServerError) continue;

                map.set(`${error.r}:${error.c}`, { r: error.r, c: error.c, msg: error.msg, kind: error.kind });
            }

            for (const [r, bucket] of this._serverRowErrors) {
                for (const error of bucket) {
                    const key = `${r}:${error.c}`;
                    if (!map.has(key)) map.set(key, { r, c: error.c, msg: error.msg, kind: error.kind });
                }
            }

            for (const error of crossRowErrors) {
                if (!error.preferServerError) continue;

                const key = `${error.r}:${error.c}`;

                if (!map.has(key)) map.set(key, { r: error.r, c: error.c, msg: error.msg, kind: error.kind });
            }

            this.errors = [...map.values()].sort((a, b) => (a.r - b.r) || (a.c - b.c));

            if (this.errors.length === 0) this.clearFilterState();

            if (this.cursor >= this.errors.length) this.cursor = this.errors.length - 1;
        },

        async runImport() {
            if (this.errors.length > 0 || this.sheetError || this.busy || this.gridLoading) return;

            this.busy        = true;
            this.importError = '';

            this.render();

            let result;
            try {
                result = await this.$wire.importRows(this.headers, this.rows);
            } catch {
                this.busy = false;
                this.importError = 'The import request failed. Nothing was saved.';
                this.render();
                return;
            }

            this.busy = false;
            result = result ?? {};

            if (result.ok === false) {
                if (Array.isArray(result.errors)) {
                    this._absorbServerErrors(result.errors, null);
                    this._rebuildErrors();
                }
                this.importError = result.message
                    ?? 'Some rows no longer pass validation. Nothing was imported.';
                this.render();
                return;
            }

            if (result.queued) {
                this.onImportQueued(result.batchId, result.total);
                return;
            }

            this.onImportComplete(result.created ?? this.rows.length);
        },

        onImportQueued(batchId, total) {
            this._stopPolling();

            this.importBatchId   = batchId;
            this.importTotal     = total;
            this.importError     = '';
            this.importDone      = 0;
            this.importChunks    = 0;
            this.reviewStep      = 'importing';
            this._pollAttempts   = 0;

            if (this.$refs.importingMsg) {
                this.$refs.importingMsg.textContent =
                    `Importing ${this.importTotal} rows in the background…`;
            }

            this._schedulePoll();
        },

        _schedulePoll() {
            const generation = this._pollGeneration;

            this._importPoll = setTimeout(() => {
                this._importPoll = null;

                if (generation !== this._pollGeneration) return;

                this._pollBatch();
            }, POLL_INTERVAL_MS);
        },

        async _pollBatch() {
            const generation = this._pollGeneration;

            if (++this._pollAttempts > MAX_POLL_ATTEMPTS) {
                this._stopPolling();
                this._failImport(`This import is taking longer than expected. Check the ${entityListLabel} before retrying.`);
                return;
            }

            let status;
            try {
                status = await this.$wire.getImportBatchStatus(this.importBatchId);
            } catch {
                if (generation !== this._pollGeneration) return;

                this._stopPolling();
                this._failImport(`Lost contact with the import. Check the ${entityListLabel} before retrying.`);
                return;
            }

            if (generation !== this._pollGeneration) return;

            if (status) {
                this.importChunks = status.chunks ?? this.importChunks;
                this.importDone   = status.processed ?? this.importDone;
            }

            if (!status?.done) {
                this._schedulePoll();

                return;
            }

            this._stopPolling();

            let result;
            try {
                result = await this.$wire.finalizeChunkedImport(this.importBatchId);
            } catch {
                this._failImport(`The import finished but could not be confirmed. Check the ${entityListLabel} before retrying.`);
                return;
            }

            result = result ?? {};

            if (result.ok === false) {
                this._failImport(
                    result.message
                        ?? status.message
                        ?? 'The import failed and nothing was saved.'
                );
                return;
            }

            this.onImportComplete(result.created ?? this.importTotal);
        },

        _failImport(message) {
            this.importBatchId = null;
            this.importChunks  = 0;
            this.importDone    = 0;
            this.importError   = message;

            this.announce(message);

            this.reviewStep = 'grid';
            this.render();
        },

        _stopPolling() {
            if (this._importPoll !== null) {
                clearTimeout(this._importPoll);
                this._importPoll = null;
            }
            this._pollAttempts = 0;
            this._pollGeneration++;
        },

        errorIndex() {
            if (this._errorIndexSource !== this.errors) {
                this._errorIndexCache  = buildErrorIndex(this.errors);
                this._errorIndexSource = this.errors;
            }

            return this._errorIndexCache;
        },

        _filteredRows()           { return filteredRowIndexes(this); },
        _filteredOffset(rowIndex) { return filteredRowOffset(this, rowIndex); },
        _visibleRows()            { return visibleRowIndexes(this); },
        _scopeRows()              { return [...scopeRowSet(this)].sort((a, b) => a - b); },

        _markRowsChanged(rowIndexes = null) {
            this._rowsVersion += 1;

            const cache = this._searchCache;

            if (!cache) return;

            if (rowIndexes === null) {
                this._searchCache = null;

                return;
            }

            for (const rowIndex of rowIndexes) {
                if (rowMatchesTerm(this.rows[rowIndex], cache.term)) cache.matches.add(rowIndex);
                else cache.matches.delete(rowIndex);
            }

            cache.version = this._rowsVersion;
        },

        _scrollCellIntoView(rowIndex, colIndex) {
            const wrap = this.$refs.gridWrap;
            const grid = this.$refs.grid;
            const cell = grid?.querySelector(`td[data-r="${rowIndex}"][data-c="${colIndex}"]`);

            if (!wrap || !cell) return;

            const headerHeight = grid.querySelector('thead')?.offsetHeight ?? 0;
            const pinnedCell   = colIndex === 0 ? null : grid.querySelector('tbody td.pin');
            const pinnedWidth  = pinnedCell
                ? pinnedCell.offsetLeft + pinnedCell.offsetWidth
                : 0;

            const cellTop    = cell.offsetTop;
            const cellBottom = cellTop + cell.offsetHeight;

            if (cellTop - headerHeight < wrap.scrollTop) {
                wrap.scrollTop = Math.max(0, cellTop - headerHeight - SCROLL_MARGIN);
            } else if (cellBottom > wrap.scrollTop + wrap.clientHeight) {
                wrap.scrollTop = cellBottom - wrap.clientHeight + SCROLL_MARGIN;
            }

            const cellLeft  = cell.offsetLeft;
            const cellRight = cellLeft + cell.offsetWidth;

            if (cellLeft - pinnedWidth < wrap.scrollLeft) {
                wrap.scrollLeft = Math.max(0, cellLeft - pinnedWidth - SCROLL_MARGIN);
            } else if (cellRight > wrap.scrollLeft + wrap.clientWidth) {
                wrap.scrollLeft = cellRight - wrap.clientWidth + SCROLL_MARGIN;
            }
        },

        goToPage(p) {
            const max = Math.max(0, this.totalPageCount - 1);
            this.page = Math.max(0, Math.min(p, max));
            this.render();
            this.$refs.gridWrap?.scrollTo({ top: 0, behavior: 'smooth' });
            this._returnKeyboardToGrid();
        },

        _returnKeyboardToGrid() {
            this.$refs.overlay?.focus({ preventScroll: true });
        },

        render() {
            const errAt   = new Map(this.errors.map(e => [`${e.r}:${e.c}`, e.msg]));
            const bad     = this.errorIndex().errorRows;
            const colErrs = new Map();
            this.errors.forEach(e => colErrs.set(e.c, (colErrs.get(e.c) ?? 0) + 1));

            const rangeIndex    = this._selectionIndex();
            const allFiltered   = this._filteredRows();
            const rowIdx        = this.rows.length > this.pageSize
                ? allFiltered.slice(this.page * this.pageSize, (this.page + 1) * this.pageSize)
                : allFiltered;
            this.totalPageCount = Math.max(1, Math.ceil(allFiltered.length / this.pageSize));
            const visible       = rowIdx.map(r => ({ row: this.rows[r], r }));

            let html = '<thead><tr>'
                + '<th class="gutter" title="Row status"></th>'
                + '<th class="rownum">#</th>';
            this.headers.forEach((header, c) => {
                const colErrCount = colErrs.get(c) ?? 0;
                const badge       = colErrCount
                    ? `<button class="gfr-col-errs" data-colerr="${c}" title="${colErrCount} error${colErrCount > 1 ? 's' : ''} in this column — click to isolate">${colErrCount}</button>`
                    : '';
                const columnSelected = rangeIndex && rangeIndex.columnSet.has(c) ? ' colsel' : '';
                html += `<th data-col="${c}" title="Click to select this column — hold Cmd/Ctrl to add it to the selection" class="${c === 0 ? 'pin' : ''}${columnSelected}"><span class="gfr-th-inner">${esc(header)}${badge}</span></th>`;
            });
            html += '</tr></thead><tbody>';

            if (!visible.length) {
                const emptyMsg = this.rows.length ? 'No rows match this filter.' : 'This file has no rows.';
                html += `<tr><td colspan="${this.headers.length + 2}" style="padding:26px;text-align:center;color:#797a7c;font-size:12px;cursor:default;">${emptyMsg}</td></tr>`;
            }

            visible.forEach(({ row, r }) => {
                const rowBad    = bad.has(r);
                const rowBlocks = rangeIndex
                    ? rangeIndex.blocks.filter(block => block.rowSet.has(r))
                    : [];
                html += `<tr><td class="gutter" title="${rowBad ? 'Blocks the import' : 'Ready to import'}"><span class="gfr-rowdot ${rowBad ? 'bad' : ''}"></span></td><td class="rownum">${r + 1}</td>`;
                this.headers.forEach((header, c) => {
                    const errMsg = errAt.get(`${r}:${c}`);
                    const val    = row[c] ?? '';
                    const isSel  = this.sel && this.sel.r === r && this.sel.c === c;
                    const inRange = !rangeIndex?.excluded.has(`${r}:${c}`)
                        && rowBlocks.some(block => c >= block.fromCol && c <= block.toCol);
                    const cls    = [
                        c === 0 ? 'pin' : '',
                        errMsg  ? 'bad' : '',
                        isSel   ? 'sel' : '',
                        inRange ? 'rng' : '',
                        val === '' ? 'empty' : '',
                    ].filter(Boolean).join(' ');
                    html += `<td data-r="${r}" data-c="${c}" class="${cls}">
<span class="gfr-cell-inner"><span class="gfr-cell-val">${esc(val)}</span>
${errMsg ? '<span class="gfr-cell-flag"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1"><circle cx="12" cy="12" r="9"/><path d="M12 7v6" stroke-linecap="round"/><circle cx="12" cy="16.5" r="1.1" fill="currentColor" stroke="none"/></svg></span>' : ''}
</span></td>`;
                });
                html += '</tr>';
            });

            this.$refs.grid.innerHTML = html + '</tbody>';

            this.renderFilterStrip(rowIdx.length, allFiltered);
            this.renderErrors();
            this.renderFooter(allFiltered.length);
            this.renderCellPop();
        },

        renderFilterStrip(shown, filteredRows = null) {
            this.$refs.filterStrip.hidden = !this.filterErrors;

            if (!this.filterErrors) {
                this.$refs.filterText.textContent = '';
                this.$refs.filterTidy.hidden      = true;

                return;
            }

            const live     = scopeRowSet(this);
            const filtered = filteredRows ?? this._filteredRows();

            let cleared = 0;

            for (const rowIndex of filtered) {
                if (!live.has(rowIndex)) cleared++;
            }

            this.$refs.filterText.textContent =
                `Showing ${shown} of ${this.rows.length} rows — only rows with an error.`
                + (cleared ? ` ${cleared} now pass${cleared === 1 ? 'es' : ''}.` : '');

            this.$refs.filterTidy.hidden = !cleared;
        },

        renderCellPop() {
            document.getElementById('gfr-cell-pop')?.remove();

            const target = this.flagHover ?? (this.hasMultiSelection ? null : this.sel);

            if (!target) return;
            const { r, c } = target;
            const hit = this.errors.find(e => e.r === r && e.c === c);
            if (!hit) return;
            const td = this.$refs.grid.querySelector(`td[data-r="${r}"][data-c="${c}"]`);
            if (!td) return;

            const wrap = this.$refs.gridWrap;
            const pop  = document.createElement('div');
            pop.className = 'gfr-cell-pop';
            pop.id        = 'gfr-cell-pop';
            pop.innerHTML = `<span class="pop-field">${esc(this.headers[c])}</span>${esc(hit.msg)}`;
            wrap.appendChild(pop);

            const maxLeft   = Math.max(4, wrap.scrollWidth - pop.offsetWidth - 8);
            const left      = Math.max(4, Math.min(td.offsetLeft, maxLeft));
            const below     = td.offsetTop + td.offsetHeight + 6;
            const fitsBelow = below + pop.offsetHeight < wrap.scrollTop + wrap.clientHeight - 4;

            pop.style.left = left + 'px';
            pop.style.top  = fitsBelow
                ? below + 'px'
                : Math.max(0, td.offsetTop - pop.offsetHeight - 6) + 'px';
            if (!fitsBelow) pop.classList.add('above');

            const arrow = Math.min(
                Math.max(td.offsetLeft - left + 14, 8),
                Math.max(8, pop.offsetWidth - 16),
            );
            pop.style.setProperty('--arrow', arrow + 'px');
        },

        renderErrors() {
            const errorCount = this.errors.length;
            const blocked    = Boolean(this.sheetError);

            this.$refs.errBar.classList.toggle(
                'clean',
                errorCount === 0 && !blocked && !this.importError && !this.gridLoading,
            );
            this.$refs.barCount.textContent = this.gridLoading
                ? 'Checking…'
                : blocked
                    ? 'File rejected'
                    : errorCount === 0
                        ? 'All rows pass'
                        : (errorCount === 1 ? '1 error' : `${errorCount} errors`);

            const filterOn = this.filterErrors || this.filterCol !== null;
            this.$refs.btnFilter.classList.toggle('on', filterOn);

            this.$refs.barPos.textContent = errorCount === 0 ? '' : `${this.cursor + 1 || '–'} / ${errorCount}`;

            const currentError = this.cursor > -1 ? this.errors[this.cursor] : null;

            if (this.gridLoading) {
                this.$refs.barCurrent.innerHTML =
                    '<span class="cur-idle">Checking every row against the import rules…</span>';
            } else if (blocked) {
                this.$refs.barCurrent.innerHTML = `<span class="cur-msg">${esc(this.sheetError)}</span>`;
            } else if (this.importError) {
                this.$refs.barCurrent.innerHTML = `<span class="cur-msg">${esc(this.importError)}</span>`;
            } else if (errorCount === 0) {
                this.$refs.barCurrent.innerHTML =
                    '<span class="cur-idle">Every row passes validation — ready to import.</span>';
            } else if (currentError) {
                this.$refs.barCurrent.innerHTML =
                    `<span class="cur-where">Row ${currentError.r + 1} · ${esc(this.headers[currentError.c])}</span>` +
                    `<span class="cur-msg">${esc(currentError.msg)}</span>`;
            } else {
                this.$refs.barCurrent.innerHTML =
                    '<span class="cur-idle">Select a red cell and press Enter to fix it, or step through with the arrows.</span>';
            }
        },

        renderFooter(totalFiltered) {
            const total       = this.rows.length;
            const badRowCount = this.errorIndex().errorRows.size;
            const blocked     = Boolean(this.sheetError);

            this.$refs.chipName.textContent  = this.fileName;
            this.$refs.chipShape.textContent = `${total} rows · ${this.headers.length} columns`;

            this.$refs.footNote.innerHTML = this.gridLoading
                ? `<b>${total}</b> rows · checking…`
                : `<b>${total}</b> rows · <b class="bad-count">${this.errors.length}</b> unresolved errors`;

            this.$refs.btnCommit.disabled  = this.errors.length > 0 || blocked || this.busy || this.gridLoading;
            this.$refs.btnCommit.innerHTML = this.busy
                ? `${BUTTON_SPINNER}Importing…`
                : `Import ${total} rows`;
            this.$refs.footHint.textContent  = this.gridLoading
                ? ''
                : blocked
                ? '· This file cannot be imported as-is.'
                : this.errors.length === 0
                    ? ''
                    : `· Nothing imports until every row passes — ${badRowCount} of ${total} rows still ${badRowCount === 1 ? 'has' : 'have'} an error.`;

            if (this.$refs.searchCount) {
                this.$refs.searchCount.textContent = this.search.trim() === ''
                    ? ''
                    : `${totalFiltered} of ${total} rows match`;
            }

            if (this.$refs.pageInfo) {
                this.$refs.pageInfo.textContent =
                    this.totalPageCount > 1
                        ? `Page ${this.page + 1} / ${this.totalPageCount}`
                        : '';
            }
        },

        goToError(i) {
            if (!this.errors.length) return;
            this.cursor = (i + this.errors.length) % this.errors.length;
            const error = this.errors[this.cursor];
            this.sel    = { r: error.r, c: error.c };

            if (this.filterCol !== null && error.c !== this.filterCol) this.filterCol = null;

            if (this.rows.length > this.pageSize) {
                const filtered   = this._filteredRows();
                const pageOffset = filtered.indexOf(error.r);
                if (pageOffset >= 0) {
                    this.page = Math.floor(pageOffset / this.pageSize);
                }
            }

            this.render();
            this._scrollCellIntoView(error.r, error.c);
            this.announce(`Row ${error.r + 1}, ${this.headers[error.c]}: ${error.msg}`);
            this.$refs.overlay?.focus({ preventScroll: true });
        },

        nextError() { this.goToError(this.cursor + 1); },
        prevError() { this.goToError(this.cursor <= -1 ? -1 : this.cursor - 1); },

        clearFilterState() {
            this.filterErrors = false;
            this.filterCol    = null;
            this.filterKeep   = null;
        },

        setSearch(value) {
            this.search = String(value ?? '');
            this.page   = 0;
            this._resetSelection();
            this.render();
            this.announce(
                this.search.trim() === ''
                    ? 'Showing all rows.'
                    : `${this._filteredRows().length} rows match "${this.search.trim()}".`,
            );
        },

        clearSearch() {
            if (this.search === '') return false;

            this.setSearch('');
            this._returnKeyboardToGrid();

            return true;
        },

        get hasMultiSelection() {
            return this.rangeBlocks.length > 0 || this.range !== null;
        },

        get selectedCellCount() {
            const index = this._selectionIndex();

            if (index) return index.cellCount;

            return this.sel ? 1 : 0;
        },

        get selectionSummary() {
            const cells = this.selectedCellCount;
            const rows  = this.selectedRowCount;

            return `${cells} ${cells === 1 ? 'cell' : 'cells'} · ${rows} ${rows === 1 ? 'row' : 'rows'} selected`;
        },

        get selectedRowCount() {
            const index = this._selectionIndex();

            if (index) return index.rowCount;

            return this.sel ? 1 : 0;
        },

        _buildRange(anchor, target) {
            const filtered     = this._filteredRows();
            const anchorOffset = this._filteredOffset(anchor.r);
            const targetOffset = this._filteredOffset(target.r);

            if (anchorOffset === -1 || targetOffset === -1) return null;

            const [fromOffset, toOffset] = anchorOffset <= targetOffset
                ? [anchorOffset, targetOffset]
                : [targetOffset, anchorOffset];
            const [fromColumn, toColumn] = anchor.c <= target.c
                ? [anchor.c, target.c]
                : [target.c, anchor.c];

            const columns = [];

            for (let column = fromColumn; column <= toColumn; column++) columns.push(column);

            return { rows: filtered.slice(fromOffset, toOffset + 1), columns };
        },

        selectRangeTo(target, additive = false) {
            if (!this.sel) this.sel = { r: target.r, c: target.c };

            const range = this._buildRange(this.sel, target);

            if (!range) return;

            if (additive) {
                this._includeAgain(range);
            } else {
                this.rangeBlocks   = [];
                this.rangeExcluded = new Set();
            }

            this.range      = range.rows.length === 1 && range.columns.length === 1 ? null : range;
            this.rangeFocus = this.range ? { r: target.r, c: target.c } : null;
            this.cursor     = this.errors.findIndex(e => e.r === target.r && e.c === target.c);

            this.render();
            this._scrollCellIntoView(target.r, target.c);
            this.announce(
                this.hasMultiSelection ? `${this.selectionSummary}.` : 'One cell selected.',
            );
        },

        toggleCellSelection(target) {
            if (this._isInRange(target.r, target.c)) {
                this.removeCellFromSelection(target);

                return;
            }

            this.addCellToSelection(target);
        },

        addCellToSelection(target) {
            if (this._isInRange(target.r, target.c)) return;
            if (this.sel && this.sel.r === target.r && this.sel.c === target.c
                && !this.rangeExcluded.has(`${target.r}:${target.c}`)) {
                return;
            }

            this._includeAgain({ rows: [target.r], columns: [target.c] });
            this._commitActiveBlock();

            this.range      = null;
            this.rangeFocus = null;
            this.sel        = { r: target.r, c: target.c };
            this.cursor     = this.errors.findIndex(e => e.r === target.r && e.c === target.c);

            this.render();
            this._scrollCellIntoView(target.r, target.c);
            this.announce(`${this.selectionSummary}.`);
        },

        _commitActiveBlock() {
            const active = this.range
                ?? (this.sel ? { rows: [this.sel.r], columns: [this.sel.c] } : null);

            if (!active) return;

            this.rangeBlocks = [...this.rangeBlocks, active];
        },

        removeCellFromSelection(target) {
            const excluded = new Set(this.rangeExcluded);

            excluded.add(`${target.r}:${target.c}`);
            this.rangeExcluded = excluded;

            if (this.selectedCellCount === 0) {
                this._resetSelection();
                this.render();
                this.announce('Selection cleared.');

                return;
            }

            this.render();
            this.announce(`${this.selectionSummary}.`);
        },

        _includeAgain(block) {
            if (!this.rangeExcluded.size) return;

            const rowSet = new Set(block.rows);
            const kept   = new Set();

            let fromCol = Infinity;
            let toCol   = -Infinity;

            for (const columnIndex of block.columns) {
                if (columnIndex < fromCol) fromCol = columnIndex;
                if (columnIndex > toCol)   toCol   = columnIndex;
            }

            for (const key of this.rangeExcluded) {
                const [rowIndex, columnIndex] = key.split(':').map(Number);

                if (columnIndex >= fromCol && columnIndex <= toCol && rowSet.has(rowIndex)) continue;

                kept.add(key);
            }

            this.rangeExcluded = kept;
        },

        selectColumn(columnIndex, additive = false) {
            const filtered = this._filteredRows();

            if (!filtered.length) return;

            const column = { rows: filtered.slice(), columns: [columnIndex] };

            if (additive) {
                this._includeAgain(column);
                this._commitActiveBlock();
            } else {
                this.rangeBlocks   = [];
                this.rangeExcluded = new Set();
            }

            this.sel        = { r: filtered[0], c: columnIndex };
            this.range      = filtered.length > 1
                ? { rows: filtered.slice(), columns: [columnIndex] }
                : null;
            this.rangeFocus = this.range ? { r: filtered[filtered.length - 1], c: columnIndex } : null;
            this.cursor     = this.errors.findIndex(e => e.r === this.sel.r && e.c === columnIndex);

            this.render();
            this.announce(
                additive
                    ? `${this.selectionSummary}.`
                    : `${filtered.length} cells selected in ${this.headers[columnIndex] ?? 'column'}.`,
            );
            this._returnKeyboardToGrid();
        },

        _resetSelection() {
            this.range         = null;
            this.rangeBlocks   = [];
            this.rangeExcluded = new Set();
            this.rangeFocus    = null;
        },

        clearRange() {
            if (!this.hasMultiSelection) return false;

            this._resetSelection();
            this.render();
            this.announce('Selection cleared.');

            return true;
        },

        _selectionBlocks() {
            if (!this.rangeBlocks.length) return this.range ? [this.range] : [];

            const blocks = [...this.rangeBlocks];

            if (this.range) blocks.push(this.range);
            else if (this.sel) blocks.push({ rows: [this.sel.r], columns: [this.sel.c] });

            return blocks;
        },

        _selectionIndex() {
            const blocks    = this.rangeBlocks;
            const excluded  = this.rangeExcluded;
            const selection = blocks.length ? this.sel : null;
            const cached    = this._selectionIndexCache;

            if (cached
                && cached.range    === this.range
                && cached.blocks   === blocks
                && cached.excluded === excluded
                && cached.sel      === selection) {
                return cached.index;
            }

            const index = this._buildSelectionIndex();

            this._selectionIndexCache = { range: this.range, blocks, excluded, sel: selection, index };

            return index;
        },

        _buildSelectionIndex() {
            const blocks = this._selectionBlocks();

            if (!blocks.length) return null;

            const columnSet     = new Set();
            const indexedBlocks = blocks.map((block) => {
                let fromCol = Infinity;
                let toCol   = -Infinity;

                for (const columnIndex of block.columns) {
                    columnSet.add(columnIndex);

                    if (columnIndex < fromCol) fromCol = columnIndex;
                    if (columnIndex > toCol)   toCol   = columnIndex;
                }

                return { rowSet: new Set(block.rows), fromCol, toCol };
            });

            let fromCol = Infinity;
            let toCol   = -Infinity;

            for (const block of indexedBlocks) {
                if (block.fromCol < fromCol) fromCol = block.fromCol;
                if (block.toCol   > toCol)   toCol   = block.toCol;
            }

            const excluded              = this.rangeExcluded;
            const [cellCount, rowCount] = this._countSelected(indexedBlocks, excluded);

            return { blocks: indexedBlocks, columnSet, excluded, fromCol, toCol, cellCount, rowCount };
        },

        _countSelected(blocks, excluded) {
            if (blocks.length === 1) {
                const [block]   = blocks;
                const rowCount  = block.rowSet.size;
                const columnCount = block.toCol - block.fromCol + 1;
                const droppedPerRow = new Map();

                for (const key of excluded) {
                    const [rowIndex, columnIndex] = key.split(':').map(Number);

                    if (columnIndex < block.fromCol || columnIndex > block.toCol) continue;
                    if (!block.rowSet.has(rowIndex)) continue;

                    droppedPerRow.set(rowIndex, (droppedPerRow.get(rowIndex) ?? 0) + 1);
                }

                let droppedCells = 0;
                let droppedRows  = 0;

                for (const count of droppedPerRow.values()) {
                    droppedCells += count;

                    if (count === columnCount) droppedRows += 1;
                }

                return [rowCount * columnCount - droppedCells, rowCount - droppedRows];
            }

            const cellKeys = new Set();
            const rowKeys  = new Set();

            for (const block of blocks) {
                for (const rowIndex of block.rowSet) {
                    for (let columnIndex = block.fromCol; columnIndex <= block.toCol; columnIndex++) {
                        const key = `${rowIndex}:${columnIndex}`;

                        if (excluded.has(key)) continue;

                        cellKeys.add(key);
                        rowKeys.add(rowIndex);
                    }
                }
            }

            return [cellKeys.size, rowKeys.size];
        },

        _isInRange(rowIndex, columnIndex) {
            const index = this._selectionIndex();

            if (index === null) return false;
            if (columnIndex < index.fromCol || columnIndex > index.toCol) return false;
            if (index.excluded.has(`${rowIndex}:${columnIndex}`)) return false;

            return index.blocks.some(block => columnIndex >= block.fromCol
                && columnIndex <= block.toCol
                && block.rowSet.has(rowIndex));
        },

        _padRow(rowIndex) {
            const row = this.rows[rowIndex];

            while (row.length < this.headers.length) row.push('');
        },

        _extendRange(key) {
            const filtered = this._filteredRows();
            const focus    = this.rangeFocus ?? this.sel;

            if (!focus) return;

            let rowOffset = this._filteredOffset(focus.r);

            if (rowOffset === -1) return;

            let columnIndex = focus.c;

            if (key === 'ArrowDown')  rowOffset   = Math.min(filtered.length - 1, rowOffset + 1);
            if (key === 'ArrowUp')    rowOffset   = Math.max(0, rowOffset - 1);
            if (key === 'ArrowRight') columnIndex = Math.min(this.headers.length - 1, columnIndex + 1);
            if (key === 'ArrowLeft')  columnIndex = Math.max(0, columnIndex - 1);

            this.selectRangeTo({ r: filtered[rowOffset], c: columnIndex }, true);
        },

        async fillRange(value, forcedRows = []) {
            const index = this._selectionIndex();

            if (!index) return;

            const touched = new Set(forcedRows);

            for (const block of this._selectionBlocks()) {
                for (const rowIndex of block.rows) {
                    this._padRow(rowIndex);

                    for (const columnIndex of block.columns) {
                        if (index.excluded.has(`${rowIndex}:${columnIndex}`)) continue;
                        if ((this.rows[rowIndex][columnIndex] ?? '') === value) continue;

                        this.rows[rowIndex][columnIndex] = value;
                        touched.add(rowIndex);
                    }
                }
            }

            if (!touched.size) {
                this.render();

                return;
            }

            this._markRowsChanged(touched);

            this.gridLoading = true;
            this.render();

            const touchedRows = [...touched];
            const columnIndex = index.columnSet.size === 1 ? index.fromCol : -1;

            try {
                const handled = columnIndex !== -1
                    && await this._validateColumnFill(columnIndex, touchedRows);

                if (!handled) await this.validateEditedRows(touchedRows);
            } finally {
                this.gridLoading = false;
                this.render();
            }

            this.announce(
                `${touched.size} ${touched.size === 1 ? 'row' : 'rows'} updated.`,
            );
        },

        refreshFilterKeep() {
            this.filterKeep = (this.filterErrors || this.filterCol !== null)
                ? new Set(scopeRowSet(this))
                : null;
        },

        setFilter(on, col = null) {
            this.filterErrors = on;
            this.filterCol    = col;
            this.page         = 0;
            this._resetSelection();
            this.refreshFilterKeep();
            this.render();
            this.announce(
                on || col !== null ? 'Showing only rows with errors.' : 'Showing all rows.',
            );
            this._returnKeyboardToGrid();
        },

        toggleFilter() {
            this.setFilter(!(this.filterErrors || this.filterCol !== null));
        },

        clearFilter() { this.setFilter(false); },

        tidyFilter() {
            this.refreshFilterKeep();
            this.page = 0;
            this.render();
            this.announce('Fixed rows hidden.');
            this._returnKeyboardToGrid();
        },

        tabStep(dir) {
            const rows = this._visibleRows();
            if (!rows.length || !this.sel) return false;
            const cols    = this.headers.length;
            let rowOffset = rows.indexOf(this.sel.r);
            if (rowOffset === -1) rowOffset = 0;
            const flatIndex = rowOffset * cols + this.sel.c + dir;
            if (flatIndex < 0 || flatIndex >= rows.length * cols) return false;
            this.sel = {
                r: rows[Math.floor(flatIndex / cols)],
                c: flatIndex % cols,
            };
            this.cursor = this.errors.findIndex(e => e.r === this.sel.r && e.c === this.sel.c);
            this.render();
            this._scrollCellIntoView(this.sel.r, this.sel.c);

            return true;
        },

        editSelection(seed) {
            if (!this.sel) return;
            const td = this.$refs.grid.querySelector(
                `td[data-r="${this.sel.r}"][data-c="${this.sel.c}"]`
            );
            if (td) this.editCell(td, seed);
        },

        editCell(td, seed) {
            const row          = +td.dataset.r;
            const col          = +td.dataset.c;
            const initialValue = seed !== undefined ? seed : (this.rows[row][col] ?? '');

            td.classList.add('editing');
            td.textContent = '';

            const input = document.createElement('span');
            input.className = 'gfr-cell-edit';
            input.setAttribute('contenteditable', 'plaintext-only');
            input.setAttribute('role', 'textbox');
            input.setAttribute('aria-label', this.headers[col] ?? 'Cell');
            for (const [name, value] of NO_EXTENSION_ATTRS) input.setAttribute(name, value);

            input.textContent = String(initialValue);
            td.appendChild(input);

            input.addEventListener('paste', (event) => {
                event.preventDefault();
                const raw = (event.clipboardData ?? window.clipboardData)
                    ?.getData('text/plain') ?? '';
                const text = raw.replace(/[\r\n\t]+/g, ' ');

                if (!document.execCommand?.('insertText', false, text)) {
                    input.textContent += text;
                    placeCaretAtEnd(input);
                }
            });
            input.addEventListener('drop', event => event.preventDefault());

            input.focus({ preventScroll: true });
            placeCaretAtEnd(input);

            let settled = false;
            const commit = async (moveDelta, fromKeyboard = false) => {
                if (settled) return;
                settled     = true;
                const value = input.textContent.trim();

                this._padRow(row);
                this.rows[row][col] = value;
                this.sel            = { r: row, c: col };

                this._markRowsChanged([row]);

                if (this._isInRange(row, col)) {
                    await this.fillRange(value, [row]);
                    this._resetSelection();
                } else {
                    await this.validateEditedRow(row);
                }

                this.cursor = this.errors.findIndex(e => e.r === row && e.c === col);
                this.render();

                if (moveDelta === 'tab+') {
                    this.tabStep(1);

                    return;
                }

                if (moveDelta === 'tab-') {
                    this.tabStep(-1);

                    return;
                }

                if (moveDelta === 'step') return;

                if (this.errors.length === 0) {
                    if (fromKeyboard) this.$refs.btnCommit?.focus();

                    return;
                }

                const stillFailing = this.cursor > -1 ? this.errors[this.cursor] : null;

                if (stillFailing) {
                    this.announce(`Row ${row + 1}, ${this.headers[col]}: ${stillFailing.msg}`);

                    if (fromKeyboard) {
                        this.$refs.overlay?.focus({ preventScroll: true });
                        this._scrollCellIntoView(row, col);
                    }

                    return;
                }

                if (!fromKeyboard) return;

                const nextIndex = this.errors.findIndex(e => e.r !== row || e.c !== col);

                this.goToError(nextIndex >= 0 ? nextIndex : 0);
            };
            const cancel = () => {
                if (settled) return;
                settled = true;
                this.render();
            };

            input.addEventListener('blur', () => commit(null));

            input.addEventListener('keydown', (event) => {
                const stepping = event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight');
                if (stepping || ['Enter', 'Tab', 'Escape'].includes(event.key)) {
                    event.stopPropagation();
                }

                if (stepping) {
                    event.preventDefault();
                    commit('step', true).then(() => {
                        if (event.key === 'ArrowRight') this.nextError();
                        else this.prevError();
                    });
                    return;
                }
                if (event.key === 'Enter')  { event.preventDefault(); commit(null, true); return; }
                if (event.key === 'Tab')    { event.preventDefault(); commit(event.shiftKey ? 'tab-' : 'tab+', true); return; }
                if (event.key === 'Escape') { event.preventDefault(); cancel(); }
            });
        },

        _bindGridListeners() {
            const grid = this.$refs.grid;

            if (!grid || grid._gfrGridListeners) return;

            const onMousedown = (ev) => {
                const colErrBtn = ev.target.closest('[data-colerr]');
                const th        = ev.target.closest('th[data-col]');
                const td        = ev.target.closest('td[data-r]');
                if (td?.classList.contains('editing')) return;

                const hit = colErrBtn
                    ? { kind: 'colerr', c: +colErrBtn.dataset.colerr }
                    : td ? { kind: 'cell', r: +td.dataset.r, c: +td.dataset.c }
                    : th ? { kind: 'column', c: +th.dataset.col } : null;
                if (!hit) return;

                const additive    = (ev.metaKey || ev.ctrlKey)
                    && (hit.kind === 'cell' || hit.kind === 'column');
                const wasSelected = hit.kind === 'cell'
                    && this.sel?.r === hit.r && this.sel?.c === hit.c
                    && !this.hasMultiSelection
                    && !additive;
                const extendRange = hit.kind === 'cell' && ev.shiftKey && Boolean(this.sel);

                setTimeout(() => {
                    if (hit.kind === 'column') {
                        this.selectColumn(hit.c, additive);

                        return;
                    }

                    if (extendRange) {
                        this.selectRangeTo({ r: hit.r, c: hit.c }, additive);

                        return;
                    }

                    if (additive) {
                        this.toggleCellSelection({ r: hit.r, c: hit.c });

                        return;
                    }

                    if (hit.kind === 'colerr') {
                        this.setFilter(false, this.filterCol === hit.c ? null : hit.c);

                        if (this.filterCol === hit.c) {
                            this.goToError(this.errors.findIndex(e => e.c === hit.c));
                        }

                        return;
                    }
                    const freshTd = this.$refs.grid.querySelector(
                        `td[data-r="${hit.r}"][data-c="${hit.c}"]`
                    );
                    if (!freshTd || freshTd.classList.contains('editing')) return;

                    this.sel    = { r: hit.r, c: hit.c };
                    this.cursor = this.errors.findIndex(e => e.r === hit.r && e.c === hit.c);

                    this._resetSelection();

                    if (wasSelected) {
                        this.editCell(freshTd);
                    } else {
                        this.render();
                    }
                }, 0);
            };

            const onDblclick = (ev) => {
                const hit = ev.target.closest('td[data-r]');
                if (!hit) return;
                ev.preventDefault();
                const row = +hit.dataset.r;
                const col = +hit.dataset.c;
                const td  = this.$refs.grid.querySelector(`td[data-r="${row}"][data-c="${col}"]`);
                if (!td || td.classList.contains('editing')) return;

                if (!this._isInRange(row, col)) this._resetSelection();

                this.sel    = { r: row, c: col };
                this.cursor = this.errors.findIndex(e => e.r === row && e.c === col);
                this.editCell(td);
            };

            const flagCellUnder = (target) => {
                const td = target?.closest?.('.gfr-cell-flag')?.closest('td[data-r]');

                return td ? { r: +td.dataset.r, c: +td.dataset.c } : null;
            };

            const setFlagHover = (next) => {
                const current = this.flagHover;

                if (current?.r === next?.r && current?.c === next?.c) return;
                if (!current && !next) return;

                this.flagHover = next;
                this.renderCellPop();
            };

            const onMouseover = (ev) => {
                setFlagHover(flagCellUnder(ev.target));
            };

            const onMouseleave = () => {
                setFlagHover(null);
            };

            grid._gfrGridListeners = { onMousedown, onDblclick, onMouseover, onMouseleave };

            grid.addEventListener('mousedown', onMousedown);
            grid.addEventListener('dblclick', onDblclick);
            grid.addEventListener('mouseover', onMouseover);
            grid.addEventListener('mouseleave', onMouseleave);
        },

        _unbindGridListeners() {
            const grid      = this.$refs?.grid;
            const listeners = grid?._gfrGridListeners;

            if (!listeners) return;

            grid.removeEventListener('mousedown', listeners.onMousedown);
            grid.removeEventListener('dblclick', listeners.onDblclick);
            grid.removeEventListener('mouseover', listeners.onMouseover);
            grid.removeEventListener('mouseleave', listeners.onMouseleave);

            delete grid._gfrGridListeners;
        },

        handleImportingKeydown(event) {
            if (event.key === 'Escape') {
                event.stopPropagation();
                event.preventDefault();
                this.dismissImporting();
            }
        },

        handleGridKeydown(event) {
            if (event.key === 'Escape') {
                event.stopPropagation();
                event.preventDefault();

                if (!this.clearRange()) this.clearSearch();

                return;
            }

            if (document.activeElement?.classList.contains('gfr-cell-edit')) return;

            if (event.altKey && event.key === 'ArrowRight') { event.preventDefault(); this.nextError(); return; }
            if (event.altKey && event.key === 'ArrowLeft')  { event.preventDefault(); this.prevError(); return; }

            const focused = document.activeElement;
            if (focused && focused !== document.body && focused !== this.$refs.overlay && !focused.closest('#gfr-grid')) return;

            if (event.shiftKey && this.sel && ARROW_KEYS.includes(event.key)) {
                event.preventDefault();
                this._extendRange(event.key);

                return;
            }

            if (this.hasMultiSelection && (event.key === 'Delete' || event.key === 'Backspace')) {
                event.preventDefault();
                this.fillRange('');

                return;
            }

            if (event.key === 'ArrowDown' || event.key === 'ArrowRight') { event.preventDefault(); this.nextError(); return; }
            if (event.key === 'ArrowUp'   || event.key === 'ArrowLeft')  { event.preventDefault(); this.prevError(); return; }

            if (event.key === 'Tab') {
                if (this.tabStep(event.shiftKey ? -1 : 1)) {
                    event.preventDefault();
                } else if (this.sel) {
                    this.sel = null;
                    this.render();
                }
                return;
            }

            if (event.key === 'Enter') { event.preventDefault(); this.editSelection(); return; }

            if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
                event.preventDefault();
                this.editSelection(event.key);
            }
        },

        announce(msg) {
            if (this.$refs.liveRegion) this.$refs.liveRegion.textContent = msg;
        },
    };
}
